import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';

export interface DeliveryQueuePayload {
  programId: number;
  statusFilter: string;
  SearchText?: string;
  locationId?: string;
  activityId?: string;
}

export interface DeliveryQueueResponse {
  statusCode: number;
  message: string;
  total: number;
  queue: any[];
  counts?: {
    pending?: number;
    delivered?: number;
    checkIn?: number;
    yetToCheckIn?: number;
    todayPending?: number;
    notDelivered?: number;
  };
}

export interface DeliverPayload {
  userId: number;
  programId: number;
  status?: string;
  activityId?: string;
}

export interface DeliverResponse {
  statusCode: number;
  message: string;
}

export interface ActivityDeliveryOptionsPayload {
  programId: number;
  locationId?: string;
}

export interface ActivityDeliveryOptionItem {
  activityId: string;
  activityName: string;
  activityImage?: string | null;
  bookedCount: number;
}

export interface ActivityDeliveryOptionsResponse {
  statusCode: number;
  message: string;
  activities: ActivityDeliveryOptionItem[];
}

@Injectable({
  providedIn: 'root'
})
export class DeliveryService {
  private readonly apiService = inject(ApiService);

  /**
   * Fetches the meal delivery queue from the API
   * @param payload Contains programId, statusFilter, and optional SearchText
   * @returns Observable containing the delivery queue response
   */
  getDeliveryQueue(payload: DeliveryQueuePayload): Observable<DeliveryQueueResponse> {
    return this.apiService.post<DeliveryQueueResponse>(API_ENDPOINTS.DELIVERY.LIST, payload);
  }

  /**
   * Fetches the transport delivery queue (approved + To Center / To Home / Round Trip only).
   */
  getTransportDeliveryQueue(payload: DeliveryQueuePayload): Observable<DeliveryQueueResponse> {
    return this.apiService.post<DeliveryQueueResponse>(API_ENDPOINTS.DELIVERY.TRANSPORT_LIST, payload);
  }

  /**
   * Updates meal delivery status for a participant
   * @param payload Contains userId, programId and optional status override
   * @returns Observable containing the delivery response
   */
  deliver(payload: DeliverPayload): Observable<DeliverResponse> {
    return this.apiService.post<DeliverResponse>(API_ENDPOINTS.DELIVERY.UPDATE, payload);
  }

  /**
   * Confirms or undoes transport delivery for a participant.
   */
  deliverTransport(payload: DeliverPayload): Observable<DeliverResponse> {
    return this.apiService.post<DeliverResponse>(API_ENDPOINTS.DELIVERY.TRANSPORT_UPDATE, payload);
  }

  /**
   * Fetches today's booked activities for staff delivery home cards.
   */
  getActivityDeliveryOptions(
    payload: ActivityDeliveryOptionsPayload
  ): Observable<ActivityDeliveryOptionsResponse> {
    return this.apiService.post<ActivityDeliveryOptionsResponse>(
      API_ENDPOINTS.DELIVERY.ACTIVITY_OPTIONS,
      payload
    );
  }

  /**
   * Fetches the activity delivery queue (seniors who booked activities for today).
   */
  getActivityDeliveryQueue(payload: DeliveryQueuePayload): Observable<DeliveryQueueResponse> {
    return this.apiService.post<DeliveryQueueResponse>(API_ENDPOINTS.DELIVERY.ACTIVITY_LIST, payload);
  }

  /**
   * Confirms or undoes activity delivery for a participant.
   */
  deliverActivity(payload: DeliverPayload): Observable<DeliverResponse> {
    return this.apiService.post<DeliverResponse>(API_ENDPOINTS.DELIVERY.ACTIVITY_UPDATE, payload);
  }
}
